import { API_URL } from '../utils/globals';
import { getAuthorizationHeader } from './authService';

const getBaseApiUrl = () => {
  const base = (API_URL || '').trim();
  return base.endsWith('/') ? base : `${base}/`;
};

const getRequestHeaders = () => {
  const authHeader = getAuthorizationHeader();
  return {
    'Content-Type': 'application/json',
    ...(authHeader ? { authorization: authHeader } : {})
  };
};

/**
 * Fetches dynamic recurring order suggestions based on recent actual sales in the system.
 * Returns an empty array if none exist, so the UI can hide prompts instead of showing fake data.
 *
 * @returns {Promise<Array<{ clientId: number, clientName: string, productName: string, quantity: number, prompt: string }>>}
 */
export const fetchRecurringSuggestions = async () => {
  try {
    const authHeader = getAuthorizationHeader();
    if (!authHeader) return [];

    const response = await fetch(`${getBaseApiUrl()}agent/recurring-suggestions`, {
      method: 'GET',
      headers: getRequestHeaders()
    });

    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.suggestions) ? data.suggestions : [];
  } catch (err) {
    return [];
  }
};

/**
 * Sends a natural language order or voice transcription to the Mastra AI agent backend.
 *
 * @param {Object} options
 * @param {string} options.message - The text/transcript input from the user
 * @param {string} [options.threadId] - Continuation thread ID for multi-turn chats
 * @param {boolean} [options.opesi=false] - Whether this is a Cuenta 2 operation
 * @param {boolean} [options.isQuoteRequest=false] - Whether this is a Presupuesto
 * @param {number} [options.opesiTaxPercentageSelected=1] - Tax percentage (1 for standard sells, 0 for Cuenta 2 without tax)
 * @returns {Promise<{ status: string, reply: string, draft?: Object, ambiguities?: Array, threadId: string, isCashPayment: boolean }>}
 */
export const sendAgentMessage = async ({
  message,
  threadId,
  opesi = false,
  isQuoteRequest = false,
  opesiTaxPercentageSelected = opesi && isQuoteRequest ? 0 : 1,
  currentDraft = null
}) => {
  try {
    const response = await fetch(`${getBaseApiUrl()}agent/text-to-sell`, {
      method: 'POST',
      headers: getRequestHeaders(),
      body: JSON.stringify({
        message,
        threadId,
        opesi,
        isQuoteRequest,
        opesiTaxPercentageSelected,
        ...(currentDraft ? { currentDraft } : {})
      })
    });

    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Sesión expirada o no autorizada. Por favor, vuelve a iniciar sesión.');
      }
      throw new Error(data.error || data.message || `Error del servidor (${response.status})`);
    }

    return data;
  } catch (err) {
    if (err.name === 'TypeError' && err.message.includes('fetch')) {
      throw new Error(
        'No se pudo conectar con el servidor backend. Verifica la conexión o la URL del API.'
      );
    }
    throw err;
  }
};

/**
 * Directly confirms and creates a SellRequest (Pedido de Venta) from the prepared draft payload.
 * Optimized for mobile street use where the user does not want to manually review all line items.
 *
 * @param {Object} options
 * @param {Object} options.sellRequestPayload - Prepared sell request payload from draft
 * @param {number} [options.auditId] - AI audit record ID to link
 * @param {string} [options.threadId] - AI thread ID to link
 * @param {Array} [options.removedItems] - Items removed by the user in the assistant modal
 * @returns {Promise<{ success: boolean, sellRequest: Object }>}
 */
export const createSellRequestFromAgent = async ({
  sellRequestPayload,
  auditId,
  threadId,
  removedItems
}) => {
  try {
    const response = await fetch(`${getBaseApiUrl()}agent/create-sell-request`, {
      method: 'POST',
      headers: getRequestHeaders(),
      body: JSON.stringify({
        sellRequestPayload,
        auditId: auditId || sellRequestPayload?.auditId,
        threadId: threadId || sellRequestPayload?.threadId,
        removedItems: removedItems || []
      })
    });

    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Sesión expirada o no autorizada. Por favor, vuelve a iniciar sesión.');
      }
      throw new Error(data.error || data.message || `Error del servidor (${response.status})`);
    }

    return data;
  } catch (err) {
    if (err.name === 'TypeError' && err.message.includes('fetch')) {
      throw new Error(
        'No se pudo conectar con el servidor backend. Verifica la conexión o la URL del API.'
      );
    }
    throw err;
  }
};
